import { Apollo } from 'src/gql/generated';

/** An account reference, narrowed to what identity matching needs. */
type AccountRef = { __typename: string; id: string } | null;

/** The parts of an assignee slot a write plan depends on. */
export type AssigneeSlot = {
  id: string;
  account?: AccountRef;
  isLauncher?: boolean;
  role?: { id: string } | null;
};

/** The parts of a picked autocomplete option a write plan depends on. */
export type AssigneeOption =
  | { __typename: 'AlineAi' }
  | { __typename: 'NewCollaborator'; text: string; type: string }
  | { __typename: 'User'; id: string }
  | { __typename: 'UserDefinedRole'; id: string }
  | {
      __typename: 'WorkflowPermission';
      id: string;
      account?: AccountRef;
      isLauncher?: boolean;
      role?: { id: string } | null;
    };

/** One set-list write: what to send, and the list to show while it flies. */
export interface AssigneeWritePlan<TSlot extends AssigneeSlot> {
  assignees: Apollo.TaskAssigneeInput[];
  /** Omitted when the write creates a permission that does not exist yet. */
  nextPermissions?: TSlot[];
}

/**
 * What a pick means for the current assignee list. `needsCollaborator` is the
 * one case the caller has to resolve first: the picked person or role has no
 * workflow permission yet, and the assignment needs one.
 */
export type AssigneeToggleDecision<TSlot extends AssigneeSlot> =
  | { kind: 'write'; plan: AssigneeWritePlan<TSlot> }
  | { kind: 'needsCollaborator' }
  | { kind: 'noop' };

/**
 * Express an existing assignee slot as the mutation's input entry. It carries no role, so the
 * server keeps the row's role as it is. The launcher has no user or role, so it goes by its flag.
 */
export function toTaskAssigneeInput(permission: AssigneeSlot): Apollo.TaskAssigneeInput {
  if (permission.isLauncher) return { isLauncher: true };

  return {
    assignedRoleId: permission.role?.id ?? null,
    assignedUserId: getSlotUserId(permission),
  };
}

/**
 * Whether an assignee slot is the one a picked option refers to. A slot can be
 * matched by its permission, by the user in it, or by the role it fills, since
 * the picker offers all three shapes for the same person.
 */
export function getIsAssigneeForOption(permission: AssigneeSlot, option: AssigneeOption): boolean {
  switch (option.__typename) {
    case 'WorkflowPermission':
      return permission.id === option.id;
    case 'User':
      return getSlotUserId(permission) === option.id;
    case 'UserDefinedRole':
      return !getSlotUserId(permission) && permission.role?.id === option.id;
    case 'AlineAi':
    case 'NewCollaborator':
      return false;
  }
}

/**
 * The single toggle: picking someone the task already has removes that slot,
 * picking anyone else appends them in `role`. Every option shape runs through
 * the same match, so the pickers cannot disagree about who is already assigned.
 */
export function planAssigneeToggle<TSlot extends AssigneeSlot>(options: {
  current: TSlot[];
  option: AssigneeOption;
  role?: Apollo.TaskAssigneeRole;
}): AssigneeToggleDecision<TSlot> {
  const { current, option, role = null } = options;
  // Aline AI is a delegation toggle, not an assignee slot.
  if (option.__typename === 'AlineAi') return { kind: 'noop' };

  const assigned = current.find(permission => getIsAssigneeForOption(permission, option));
  if (assigned) {
    const nextPermissions = current.filter(permission => permission.id !== assigned.id);
    return {
      kind: 'write',
      plan: { assignees: nextPermissions.map(toTaskAssigneeInput), nextPermissions },
    };
  }

  const kept = current.map(toTaskAssigneeInput);
  if (option.__typename === 'NewCollaborator') {
    // No optimistic list: this call is what creates the permission.
    const added = option.type === 'email' ? { email: option.text } : { roleName: option.text };
    return { kind: 'write', plan: { assignees: [...kept, { ...added, role }] } };
  }

  if (option.__typename === 'WorkflowPermission')
    return {
      kind: 'write',
      plan: { assignees: [...kept, { ...toTaskAssigneeInput(option), role }] },
    };

  return { kind: 'needsCollaborator' };
}

/** Append a permission the caller just created for the picked user or role, in `role`. */
export function planAddPermission<TSlot extends AssigneeSlot>(
  current: TSlot[],
  permission: TSlot,
  role: Apollo.TaskAssigneeRole | null = null
): AssigneeWritePlan<TSlot> {
  const nextPermissions = [...current, permission];
  return {
    assignees: [...current.map(toTaskAssigneeInput), { ...toTaskAssigneeInput(permission), role }],
    nextPermissions,
  };
}

/**
 * The input entry for a picked person, role or new collaborator. The server finds or creates the
 * workflow permission for a user or role id, so nothing has to exist first. Null for Aline AI,
 * which is a delegation rather than an assignee slot.
 */
export function getPickedAssigneeInput(option: AssigneeOption): Apollo.TaskAssigneeInput | null {
  switch (option.__typename) {
    case 'WorkflowPermission':
      return toTaskAssigneeInput(option);
    case 'User':
      return { assignedRoleId: null, assignedUserId: option.id };
    case 'UserDefinedRole':
      return { assignedRoleId: option.id, assignedUserId: null };
    case 'NewCollaborator':
      return option.type === 'email' ? { email: option.text } : { roleName: option.text };
    case 'AlineAi':
      return null;
  }
}

/**
 * Add the first reviewer to a task that has no assignee, together with the assignee who does the
 * work. One write, so the task never has reviewers and nobody doing the work.
 */
export function planAddReviewerWithAssignee<TSlot extends AssigneeSlot>(
  current: TSlot[],
  people: { assignee: Apollo.TaskAssigneeInput; reviewer: Apollo.TaskAssigneeInput }
): AssigneeWritePlan<TSlot> {
  return {
    assignees: [
      ...current.map(toTaskAssigneeInput),
      { ...people.assignee, role: Apollo.TaskAssigneeRole.Assignee },
      { ...people.reviewer, role: Apollo.TaskAssigneeRole.Reviewer },
    ],
  };
}

/** Clearing the field. Null when there is nothing assigned to clear. */
export function planUnassignAll<TSlot extends AssigneeSlot>(
  current: TSlot[]
): AssigneeWritePlan<TSlot> | null {
  return current.length === 0 ? null : { assignees: [], nextPermissions: [] };
}

/** The user in a slot, if a person rather than a role holds it. */
function getSlotUserId(permission: AssigneeSlot): string | null {
  return permission.account?.__typename === 'User' ? permission.account.id : null;
}
